import React, { useMemo } from 'react';
import classNames from 'classnames';
import { keepPreviousData, useQuery } from '@tanstack/react-query';

import { useDebounce } from 'Hooks/useDebounce';
import { store } from 'Models/Store';
import { charterQueryKeys } from 'Modules/Charter/Services/charterQueryKeys';
import { fetchChartersList } from 'Modules/Charter/Services/CharterApi';
import { STAFF_TABLE_SECTIONS, useResetFiltersOnSectionEntry } from 'Util/_HumanWritten/Navigation/staffTableSections';
import { StaffChartersCollection } from './StaffChartersCollection';
import { StaffCharterFilters } from './StaffCharterFilters';
import { useStaffChartersStore } from './StaffChartersFilterStore';

const PER_PAGE = 10;

export interface StaffChartersListOuterProps {
	/** Narrows the list to one customer's charters — the charters tab on their page. */
	userId?: string;
	/** Set by the embedded copy on a customer's page: drops the heading and the customer column. */
	onResultsPage?: boolean;
}

/**
 * The charters table, in both the places it appears: the **Charters** tab on /staff-bookings, and
 * the **Charters** tab on a customer's page.
 *
 * One component and one store for the two, the way the bookings and parking reservations tables it
 * sits beside do it — so a filter added here reaches both, and a charter reads the same on either.
 * `userId` is the only thing that separates them: it scopes the read to that customer, and the
 * embedded copy then drops the customer column, which would repeat the same person down every row.
 *
 * The embedded copy also starts from its own defaults (see `resetFilters`) — a customer's page opens
 * on every state, cancelled charters included, because a cancelled charter is off the staff schedule
 * entirely and their page is the only place one can still be found.
 */
export function StaffChartersListOuter({ userId, onResultsPage }: StaffChartersListOuterProps = {}) {
	const isEmbedded = onResultsPage === true;

	/**
	 * Whether the person reading this is the customer rather than staff.
	 *
	 * /staff-bookings is two pages wearing one tab strip: the operator's whole book of business for
	 * staff, and their own for an InvoicedUser or a FixedPriceUser, who the nav sends here instead of
	 * to the booking cards. This is the same table either way — one set of filters, one sort, one
	 * pager — and the three things that differ are decided from here.
	 *
	 * Derived rather than passed in, so the component cannot be pointed at the staff endpoint on
	 * behalf of somebody who would only get a 403 from it.
	 */
	const viewerIsCustomer = !store.isStaff;

	// Above the store subscription on purpose — see useResetFiltersOnSectionEntry. Arriving from
	// anywhere outside the charters section starts the table on its defaults; coming back from a
	// charter keeps the filters and the page that led there. The embedded copy always resets, so a
	// customer's page never opens on filters set on the staff-wide table.
	useResetFiltersOnSectionEntry(
		STAFF_TABLE_SECTIONS.CHARTERS,
		() => useStaffChartersStore.getState().resetFilters(isEmbedded),
		isEmbedded,
	);

	const { filters, updateFilters } = useStaffChartersStore();

	// The page number is held out of the debounce and put back afterwards: paging should move the
	// table at once, where typing should not fetch per keystroke. Debouncing the page number too
	// would make every click of the pager wait out the delay.
	const debouncedSearchFilters = useDebounce({ ...filters, pageNo: undefined }, 150);

	const queryFilters = useMemo(
		() => ({ ...debouncedSearchFilters, pageNo: filters.pageNo }),
		[debouncedSearchFilters, filters.pageNo],
	);

	const { data, isLoading, isFetching, refetch, dataUpdatedAt } = useQuery({
		// The customer is part of the key, not just the request — without it the staff-wide table and
		// a customer's tab would share a cache entry and serve each other's rows.
		queryKey: charterQueryKeys.list(
			// A customer's own scope is not "all", and not a userId they sent — the server decides it.
			// Named here so their page cannot share a cache entry with the staff-wide read.
			viewerIsCustomer ? 'mine' : userId,
			queryFilters,
			PER_PAGE,
		),
		queryFn: () =>
			fetchChartersList({
				searchString: queryFilters.searchString,
				userId,
				startDate: queryFilters.startDate,
				endDate: queryFilters.endDate,
				dateFilterType: queryFilters.dateFilterType,
				statusFilters: queryFilters.statusFilters,
				ferryFilters: queryFilters.ferryFilters,
				orderBy: queryFilters.orderBy,
				orderByDescending: queryFilters.orderByDescending,
				skip: PER_PAGE * (queryFilters.pageNo || 0),
				take: PER_PAGE,
			}, viewerIsCustomer),
		placeholderData: keepPreviousData,
		staleTime: 1000 * 60,
	});

	return (
		// `search-results` is the embedded-in-a-customer's-page variant the shared staff table styles
		// carry, the same one the bookings table beside it is given — without it the table sits
		// shrink-to-fit and centred, with the page's grey showing down both sides.
		<div className={classNames('frontend-admin-page', 'staff-charters', isEmbedded && 'search-results')}>
			{/*
				Heading and border like the bookings and parking tables on the same page: the tab strip
				names the table, but each of the three restates it above its own filter bar. The
				embedded copy has the customer's own tab strip above it and drops them.
			*/}
			{!isEmbedded && (
				<>
					<div className="frontend-admin-page__layout--wide">
						<h1>Charters</h1>
					</div>
					<div className="page-header__border" />
				</>
			)}

			<StaffCharterFilters
				setRefresh={() => refetch()}
				hideCustomerColumn={isEmbedded || viewerIsCustomer}
				viewerIsCustomer={viewerIsCustomer}
			/>

			<StaffChartersCollection
				charters={data?.data ?? null}
				totalCharterCount={data?.count ?? 0}
				perPage={PER_PAGE}
				filters={filters}
				setFilters={updateFilters}
				isLoading={isLoading || isFetching}
				lastUpdated={dataUpdatedAt ? new Date(dataUpdatedAt) : new Date()}
				// A customer's own name down every row of their own table says nothing.
				hideCustomerColumn={isEmbedded || viewerIsCustomer}
				viewerIsCustomer={viewerIsCustomer}
			/>
		</div>
	);
}
